import { getWebPath } from '@doss/common-all'
import { Stack } from '@mui/material'
import { useNavigate } from 'react-router'
import {
  BreadcrumbObjectType,
  useDeleteWorkspaceMutation,
} from '~/common/generated/apollo-operations'
import { useOrgProjects } from '~/common/hooks/useOrgProjects'
import { useTypedTranslation } from '~/common/hooks/useTypedTranslation'
import { Button } from '~/components/ui/atoms/button/Button'
import { useDossSnackbar } from '../shared/DossSnackbar'
import { Modal } from '../ui/molecules/modal/Modal'

interface DeleteWorkspaceDialogProps {
  workspaceId: string
  openDialog: boolean
  handleCloseDialog: () => void
}

/**
 * Dialog to confirm the deletion of a workspace (dashboard)
 *
 * Upon deletion of the workspace, it is removed from cache and the user is redirected to the current project page
 */
export function DeleteWorkspaceDialog({
  workspaceId,
  openDialog,
  handleCloseDialog,
}: DeleteWorkspaceDialogProps) {
  const snackbar = useDossSnackbar()
  const navigate = useNavigate()

  const { t } = useTypedTranslation()
  const { selectedProject } = useOrgProjects()

  const [deleteWorkspace] = useDeleteWorkspaceMutation({
    update(cache, { data }) {
      if (!data) {
        return
      }
      const normalizedId = cache.identify(data.deleteWorkspace)

      cache.evict({ id: normalizedId })
      cache.gc()
    },
  })
  const handleDeleteWorkspace = () => {
    deleteWorkspace({
      variables: { workspaceId },
    })
      .then(() => {
        snackbar.showSuccess(t('charts.delete_success'))
        // Return to current project page
        navigate(
          getWebPath({ objectType: BreadcrumbObjectType.PROJECT, objectId: selectedProject?.id }),
          { replace: true },
        )
      })
      .catch((error) => {
        snackbar.showError(error.message)
      })
  }

  return (
    <Modal
      actions={
        <Button onClick={handleDeleteWorkspace} size="small" variant="danger">
          {t('workspace.delete')}
        </Button>
      }
      onClose={handleCloseDialog}
      open={openDialog}
      title={t('workspace.delete_dashboard')}
    >
      <Stack gap={1}>{t('charts.confirm_delete')}</Stack>
    </Modal>
  )
}
